import React from 'react';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { removeHistory } from '@fiji/common/src/features/common/historySlice';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import {
    useCreateWidgetMutation,
    useDeleteWidgetMutation,
    useUpdateWidgetMutation,
} from '@fiji/common/src/features/widgetManagement/widgetApi';
import {
    resetCloneWidgetModal,
    resetRenameWidgetModal,
    setCloneWidgetPayload,
    setRenameWidgetPayload,
} from '@fiji/common/src/features/widgetManagement/widgetSlice';
import { useRBAC, useNewCustomTranslations } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setSaveAsCount } from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';
import { useConfigureWidgets } from 'pages/DashboardManagement';
import {
    handleCleanDefaultComponentPayload,
    handleCleanLoadGroupsPayload,
    handleCleanLoadsPayload,
    handleCleanMapPayload,
    handleCleanTimelineDeviceListAndGroupListPayload,
    handleCleanTrendsPayload,
} from '../utils/helpers';
import { replaceWidgetList } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { useWidgetSelectedEntities } from './useWidgetSelectedEntities';
import { getArrayApiErrorMessage } from '../../../../utils/helpers';

const handleCleanGaugePayload = (clone: any): void => {
    delete clone['data']['value'];
};

const handleCleanChartsPayload = (clone: any): void => {
    clone['secondary']?.forEach((item: any) => {
        delete item.value;
    });
};

const handleCleanEnergyFlowAssets = (clone: any): void => {
    clone['assets'] = clone['assets']?.map((item: any) => ({
        id: item.id,
        deviceTypeId: item.deviceTypeId,
        name: item.name,
    }));
};

const handleCleanMetricsAssets = (clone: any): void => {
    clone['assets'] = clone['assets']?.map((item: any) => ({
        id: item?.id,
        deviceTypeId: item?.deviceTypeId,
        name: item?.name,
    }));
};

const handleCleanWeatherAssets = (clone: any): void => {
    clone['assets'] = {
        parentId: clone['assets']?.parentId,
        id: clone['assets']?.id,
        name: clone['assets']?.name,
        lat: clone['assets']?.lat,
        lng: clone['assets']?.lng,
    };
};

const CLONE_CONFIG_HANDLER_MAP: Record<string, (configClone: any) => void> = {
    timeline: handleCleanTimelineDeviceListAndGroupListPayload,
    device_list: handleCleanTimelineDeviceListAndGroupListPayload,
    group_list: handleCleanTimelineDeviceListAndGroupListPayload,
    schedule: handleCleanTimelineDeviceListAndGroupListPayload,
    score: handleCleanTimelineDeviceListAndGroupListPayload,
    upcoming: handleCleanTimelineDeviceListAndGroupListPayload,
    environmental_stress_monitoring: handleCleanTimelineDeviceListAndGroupListPayload,
    components: handleCleanTimelineDeviceListAndGroupListPayload,
    map: handleCleanMapPayload,
    gauge: handleCleanGaugePayload,
    properties: handleCleanDefaultComponentPayload,
    settings: handleCleanDefaultComponentPayload,
    details: handleCleanDefaultComponentPayload,
    charts: handleCleanChartsPayload,
    trends: handleCleanTrendsPayload,
    loads: handleCleanLoadsPayload,
    virtual_loads: handleCleanLoadsPayload,
    load_groups: handleCleanLoadGroupsPayload,
    load_group_trends: handleCleanLoadGroupsPayload,
    energy_flow: handleCleanEnergyFlowAssets,
    metrics: handleCleanMetricsAssets,
    consumption_breakdown: handleCleanMetricsAssets,
    load_trends: handleCleanMetricsAssets,
    weather: handleCleanWeatherAssets,
};

type ReturnProps = {
    payload: any;
    isLoading: boolean;
    handleCloneModalAction: (...args: any) => void;
    handleCloneSave: (...args: any) => Promise<void>;
    handleDeleteModalAction: (...args: any) => void;
    handleDeleteConfirm: (...args: any) => Promise<void>;
    handleRenameModalAction: (...args: any) => void;
    handleRenameSave: (...args: any) => Promise<void>;
    cloneWidgetError?: any;
};
export const useHandleWidgetListActions = (
    selectorType: string,
    onClickCallback?: any,
    deleteModalClickCallback?: any
): ReturnProps => {
    const { t } = useTranslation();
    const location = useLocation();
    const modalPayload = useTypedSelector((state) => state.widget[selectorType]);
    const { buildSelectedEntities } = useWidgetSelectedEntities();
    const dispatch = useAppDispatch();
    useNewCustomTranslations('COMMON');
    const { widgetId } = useParams();

    const [createWidget, { isLoading, error: cloneWidgetError }] = useCreateWidgetMutation();
    const [deleteWidget] = useDeleteWidgetMutation();
    const [updateWidget, { isLoading: updateWidgetLoader }] = useUpdateWidgetMutation();

    const navigate = useNavigate();

    const permissions = useTypedSelector(selectCurrentPermission);

    const saveAsCount = useTypedSelector((state) => state['widgetConfiguration']['saveAsCount']);

    const { hasPermission } = useRBAC(permissions);
    const canCreateUserWidgets = hasPermission('widget-management_view-user-widgets');
    const canCreateSystemWidgets = hasPermission('widget-management_create-system-widgets');

    const { widgetConfigHandler } = useConfigureWidgets(true);

    React.useEffect(() => {
        if (canCreateUserWidgets && !canCreateSystemWidgets) {
            dispatch(setCloneWidgetPayload({ key: 'isSystemWidget', value: false }));
        }
    }, [canCreateSystemWidgets, canCreateUserWidgets]);

    const handleCloneModalAction = (action: boolean, data: any): void => {
        if (action && data) {
            const presetPayload = {
                categoryId: data?.categoryId ?? '',
                ...(data?.config && { config: data?.config }),
                name: `${data?.name}-copy`,
                description: data?.description,
                isSystemWidget: data?.isSystemWidget,
                ...(data?.id && !data?.config && !data?.referenceWidgetId && { referenceWidgetId: data?.id }),
                ...(data?.referenceWidgetId && { referenceWidgetId: data?.referenceWidgetId }),
                sourceId: data?.sourceId,
                sourceName: data?.sourceName,
                type: data?.widgetType?.type ?? 'PREDEFINED',
                widgetTypeId: data?.widgetType?.id,
            };
            onClickCallback(data);

            for (const key in presetPayload) {
                dispatch(setCloneWidgetPayload({ key: key, value: presetPayload[key] }));
            }
        }
    };

    const handleDeleteModalAction = (action: boolean, widgetData: any): any => {
        if (action) {
            onClickCallback(widgetData);
            return;
        }
        deleteModalClickCallback(widgetData);
    };

    const handleRenameModalAction = (action: boolean, data: any): void => {
        if (action && data) {
            onClickCallback(data);
            const presetPayload: any = {
                name: data?.name,
                description: data?.description,
            };
            for (const key in presetPayload) {
                dispatch(setRenameWidgetPayload({ key: key, value: presetPayload[key] }));
            }
        }
    };

    const handleCloneSave = async (modalType: any, onCancelCallback: any): Promise<void> => {
        const modalPayloadClone = JSON.parse(JSON.stringify(modalPayload.payload));
        if (canCreateUserWidgets && !canCreateSystemWidgets) {
            modalPayloadClone.isSystemWidget = false;
        }
        const configClone = JSON.parse(JSON.stringify(modalPayloadClone?.config ?? {}));

        if (modalType === 'save' && Object.keys(configClone)?.length) {
            CLONE_CONFIG_HANDLER_MAP[location?.state?.typeId]?.(configClone);

            if (location?.state?.typeId === 'timeline' || location?.state?.typeId === 'device_list') {
                delete configClone['secondary'];
                delete configClone['statusCount'];
            }
            modalPayloadClone['config'] = configClone;
        }

        const widgetTypeId = location?.state?.typeId ?? modalPayloadClone?.widgetTypeId;
        if (widgetTypeId) {
            modalPayloadClone['selectedEntities'] = buildSelectedEntities({
                widgetTypeId,
                cleanedConfig: modalPayloadClone?.config,
                rawConfig: modalPayloadClone?.config,
                sourceId: modalPayloadClone?.sourceId ?? null,
            });
        }

        const data: any = await createWidget(modalPayloadClone);
        if (data?.data?.success) {
            dispatch(resetCloneWidgetModal());
            onCancelCallback();
            dispatch(
                setMessageContent({
                    isOpen: true,
                    ...(modalType === 'save'
                        ? {
                              message: `${t('COMMON:CHANGES_TO')} ${data?.data?.data?.name} ${
                                  data?.data?.data?.widgetType?.id
                              } ${t('COMMON:HAVE_BEEN_SAVED')}`,
                          }
                        : { message: t('WIDGET_MANAGEMENT:WIDGET_CLONED_SUCCESSFULLY') }),
                })
            );
            if (modalType === 'save') {
                dispatch(
                    replaceWidgetList({
                        widgetId: data?.data?.data?.id,
                        destinationWidgetId: widgetId,
                        data: data?.data?.data,
                    })
                );
                const splittedArr = location.pathname.split('/');
                splittedArr[splittedArr?.length - 1] = data?.data?.data?.id;

                navigate(`${splittedArr?.join('/')}`, {
                    state: {
                        ...location?.state,
                        typeId: data?.data?.data?.widgetType?.id,
                    },
                });

                // Remove the original widget edit screen from history after navigating to new widget
                // This ensures that when user clicks close on new widget, it goes back to editDashboard
                dispatch(removeHistory(1));
                dispatch(setSaveAsCount(saveAsCount + 1));
            }
        }
    };
    const handleDeleteConfirm = async (data: any, deleteCancelCallback?: any): Promise<void> => {
        const { error }: any = await deleteWidget(data?.id);
        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:WIDGET_DELETE_SUCCESS', {
                        replace: { widgetName: data?.name },
                    }),
                })
            );
            deleteCancelCallback();
        }
    };

    const handleRenameSave = async (widgetData: any, cancelCallback: any): Promise<void> => {
        const payloadClone = JSON.parse(JSON.stringify(modalPayload?.payload ?? {}));
        if (payloadClone && !payloadClone?.description?.length) {
            payloadClone['description'] = null;
        }
        if (widgetData?.sourceId) {
            payloadClone['sourceId'] = widgetData?.sourceId;
            payloadClone['sourceName'] = widgetData?.sourceName;
        }
        const data: any = await updateWidget({ body: payloadClone, widgetId: widgetData?.id });
        const updateErrorMessage = getArrayApiErrorMessage(data?.error);
        if (updateErrorMessage) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: updateErrorMessage,
                    duration: 3000,
                })
            );
        }
        if (data?.data?.success) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('WIDGETS:RENAME_WIDGET_MODAL.SUCCESS_MSG', {
                        replace: { widgetName: widgetData?.name },
                    }),
                })
            );
            widgetConfigHandler(widgetData?.id);
            dispatch(resetRenameWidgetModal());
            cancelCallback();
        }
    };

    return {
        payload: modalPayload?.payload,
        isLoading: isLoading || updateWidgetLoader,
        handleCloneSave,
        handleDeleteConfirm,
        handleRenameSave,
        handleRenameModalAction,
        handleCloneModalAction,
        handleDeleteModalAction,
        cloneWidgetError,
    };
};
